import { useSyncExternalStore } from 'react'
import { matchPath } from 'react-router-dom'
import { findMenuEntry } from './navigation'
import { strings as login } from '../features/login/strings'
import { strings as signup, activateStrings as activate } from '../features/signup/strings'
import { strings as install } from '../features/install/strings'
import { profileStrings, systemPageStrings } from '../features/account/strings'
import { reportStrings as phishingReport } from '../features/phishing/strings'
import { licenseInformationStrings } from '../features/organisations/strings'
import * as portal from '../user/strings'
import { K as kit } from './KitPage.strings'
import { isEnglish } from '../lib/i18n'

/**
 * What the BROWSER TAB says, per route.
 *
 * Every screen in the product used to share one title — the one hard-coded in
 * `index.html`, which named the INSTALL page — so a reader with six Rawam tabs
 * open had six identical ones, and browser history recorded every visit under
 * the same name.
 *
 * ---- Where the words come from ----
 *
 * Not from a new list of copy. A tab title that is written twice drifts, and the
 * app already says what each screen is called in two places:
 *
 *   the ADMIN side menu   `adminMenu` in ./navigation — labels that were curated
 *                         once and are what the reader pressed to get here;
 *   the FEATURE strings   the portal sections and the standalone screens, each
 *                         of which already carries its own `title`.
 *
 * So this file is a routing table, not a copy file. The only routes named
 * explicitly below are the ones no menu entry owns.
 *
 * ---- How a path resolves ----
 *
 * 1. `ROUTES`, in order — the first pattern that matches wins, so a longer path
 *    must be listed above the shorter one it starts with.
 * 2. `findMenuEntry`, which already answers "which menu item owns this route"
 *    by longest match — that is what gives `/users/7` the users list's name and
 *    `/programs/4/courses/new` the programmes'.
 * 3. Nothing, and the tab falls back to the application title alone.
 */

/** Patterns, most specific FIRST. `matchPath` is exact unless a pattern ends `/*`. */
const ROUTES = [
  // ---- Signed out, and the screens reached from an email ----
  ['/login/otp', login.otp],
  ['/login', login.title],
  ['/reset-password', login.resetPassword],
  ['/signup', signup.title],
  // The splat also matches `/activate` itself — a splat is zero or more segments.
  ['/activate/*', activate.title],
  ['/install', install.pageTitle],

  // ---- Standalone surfaces the legacy also renders outside the shell ----
  ['/error/:statusCode', systemPageStrings.error.title],
  ['/error', systemPageStrings.error.title],
  ['/unauthorized', systemPageStrings.unauthorized.title],
  ['/shutdown', systemPageStrings.shutdown.title],
  ['/redirect', systemPageStrings.redirect.title],

  // ---- The learner's panel ----
  ['/portal/courses/:courseId/exam', portal.exam.title],
  ['/portal/courses/:courseId', portal.courseDetails.title],
  ['/portal/courses', portal.courses.title],
  ['/portal/exam/:attemptId', portal.exam.title],
  ['/portal/exam-history', portal.examHistory.title],
  ['/portal/awareness/:articleId', portal.awareness.title],
  ['/portal/awareness', portal.awareness.title],
  ['/portal/assessments', portal.assessments.title],
  ['/portal/surveys/:surveyId', portal.surveys.title],
  ['/portal/surveys', portal.surveys.title],
  ['/portal/policies', portal.policies.title],
  ['/portal/reports', portal.reports.title],
  ['/portal/leaderboard', portal.leaderboard.title],
  ['/portal/certificates', portal.certificates.title],
  ['/portal/profile/password', portal.shell.changePassword],
  ['/portal/profile', portal.profile.title],
  ['/portal/contact', portal.contact.title],
  ['/portal/manual', portal.manual.title],
  ['/portal/terms', portal.terms.title],
  ['/portal', portal.dashboard.title],

  // ---- Admin routes no menu entry owns ----
  ['/phishing/report/:campaignId', phishingReport.title],
  ['/phishing/report', phishingReport.title],
  ['/license-information', licenseInformationStrings.title],
  ['/profile', profileStrings.title],
  ['/_kit', kit.title],
]

/** The bundled application name, used until the session reports the real one. */
export const FALLBACK_APP_NAME = isEnglish ? 'Rawam' : 'روام'

/* ---- The name, remembered for screens that have no session ----------------------

   RENAMING THE PLATFORM NEVER REACHED A SIGNED-OUT SCREEN. The login, reset and sign-up
   pages printed the built-in "روام", and so did their tabs, because the name lives in the
   session and a visitor has none. The public login payload carries both names; they are
   kept here (and in storage, so the next visit's first paint already has them) for every
   screen that has nothing better. */

const APP_NAME_KEY = 'rawam.appName'
const nameListeners = new Set()

function readRememberedName() {
  try {
    const value = JSON.parse(localStorage.getItem(APP_NAME_KEY) ?? 'null')
    return value && typeof value === 'object' ? value : null
  } catch {
    return null
  }
}

let rememberedName = readRememberedName()

/** Keeps the installation's name in both languages. Blank values are ignored. */
export function rememberAppName(arabic, english) {
  const next = { ar: String(arabic ?? '').trim(), en: String(english ?? '').trim() }
  if (!next.ar && !next.en) return
  if (rememberedName?.ar === next.ar && rememberedName?.en === next.en) return
  rememberedName = next
  try {
    localStorage.setItem(APP_NAME_KEY, JSON.stringify(next))
  } catch {
    /* storage unavailable — the name still holds for this page */
  }
  for (const listener of nameListeners) listener()
}

const subscribeName = (listener) => {
  nameListeners.add(listener)
  return () => nameListeners.delete(listener)
}

/** Re-renders a component when the remembered name changes. */
export const useRememberedAppName = () => useSyncExternalStore(subscribeName, () => rememberedName, () => null)

/** The name a signed-out screen shows: the remembered one, or the built-in word. */
export function publicAppName() {
  return (isEnglish ? rememberedName?.en : rememberedName?.ar) || FALLBACK_APP_NAME
}

/**
 * The installation's own name for itself.
 *
 * `GET /api/session` returns both title settings under their DATABASE keys, and
 * which one applies is a question about the reader, not the tenant — so the
 * active language picks. An install that never set them falls back to the same
 * word the login screen prints.
 */
export function appName(session) {
  const key = isEnglish ? 'Default_EnglishApplicationTitle' : 'Default_ArabicApplicationTitle'
  return session?.settings?.[key]?.trim() || publicAppName()
}

/** @returns {string|null} the page's own name, or null when nothing claims the route */
export function titleForPath(pathname) {
  for (const [pattern, title] of ROUTES) {
    if (matchPath(pattern, pathname)) return title
  }
  return findMenuEntry(pathname)?.item.title ?? null
}

/**
 * `<page> — <app>`, the page first.
 *
 * A tab is a few characters wide and truncates from the END, so the half that
 * survives has to be the half that differs. Titling every screen
 * "روام — قائمة المستخدمين" would show six tabs reading "روام — …".
 */
export function documentTitle(pathname, session) {
  const page = titleForPath(pathname)
  const app = appName(session)
  return page ? `${page} — ${app}` : app
}
